import { Actions } from '@src/types/Actions';
import { ReactElement } from 'react';
import storeConnector from '@store/storeConnector';
import { SiteMeta } from '@src/types/SiteMeta';
import LineHighChart from '@components/_elements/LineHighChart/LineHighChart';

interface ChartDataValue {
  Hz?: number;
  kW?: number;
  kVAR?: number;
  BESSkW?: number;
  PVkW?: number;
  WindkW?: number;
  [key: string]: number | undefined;
}

interface ChartDataPoint {
  date: string;
  value: ChartDataValue;
}

interface MainChartProps {
  actions: Actions;
  siteMeta: SiteMeta;
  battActive: boolean;
  pvActive: boolean;
  windActive: boolean;
  mwEnable: number;
}

function MainChart({
  actions,
  siteMeta,
  battActive,
  pvActive,
  windActive,
  mwEnable,
}: MainChartProps): ReactElement {
  const { siteId } = siteMeta;
  const { Use_New_Topic_Structure: useNewTopicStructure } = siteMeta.ui;
  const meterPointDictionary = siteMeta.ui.Meter_Point_Dictionary;

  let chartMeterFields = ['Hz', 'kW', 'kVAR'];
  if (meterPointDictionary) {
    chartMeterFields = chartMeterFields.map(
      (field) => meterPointDictionary[field] || field,
    );
  }

  return (
    <div className='cell h40 block-container'>
      <div className='in' style={{ height: '100%' }}>
        <LineHighChart
          defaultShowVariables={['Freq', 'kW']}
          hasSecondAxis
          megaVars={
            // fields
            mwEnable
              ? [
                  { prevLabel: 'kW', newLabel: 'MW', newUnit: 'MW' },
                  {
                    prevLabel: 'kVAR',
                    newLabel: 'MVAR',
                    newUnit: 'MVAR',
                  },
                  { prevLabel: 'BESS', newLabel: 'BESS', newUnit: 'MW' },
                ]
              : []
          }
          priorityVar='Freq'
          variables={{
            // 'label': 'field'
            Freq: 'Hz',
            kW: 'kW',
            kVAR: 'kVAR',
            ...(battActive ? { BESS: 'BESSkW' } : {}),
            ...(pvActive ? { PV: 'PVkW' } : {}),
            ...(windActive ? { Index: 'WindkW' } : {}),
          }}
          units={{
            // 'field': 'unit'
            Hz: 'Hz',
            kW: 'kW',
            kVAR: 'kVAR',
            ...(battActive ? { BESSkW: 'kW' } : {}),
            ...(pvActive ? { PVkW: 'kW' } : {}),
            ...(windActive ? { WindkW: 'kW' } : {}),
          }}
          minmax={{
            Freq: [
              Number(siteMeta.NomFreq) - Number(siteMeta.FreqDeviation),
              Number(siteMeta.NomFreq) + Number(siteMeta.FreqDeviation),
            ],
            kW: [
              Number(siteMeta.kWMinP) / (mwEnable ? 1000 : 1),
              (Number(siteMeta.kWMaxP) +
                Number(siteMeta.kWPV) +
                Number(siteMeta.kWWind)) /
                (mwEnable ? 1000 : 1),
            ],
            kVAR: [
              Number(siteMeta.kVArMinQ) / (mwEnable ? 1000 : 1),
              Number(siteMeta.kVArMaxQ) / (mwEnable ? 1000 : 1),
            ],
            ...(pvActive
              ? {
                  PV: [0, Number(siteMeta.kWPV)],
                }
              : {}),
            ...(windActive
              ? {
                  Index: [0, Number(siteMeta.kWWind)],
                }
              : {}),
          }}
          margins={{ top: 50 }}
          fixedYTicks={5}
          dataLoadFunction={async (
            duration: string,
          ): Promise<ChartDataPoint[]> => {
            const [sitePointData, meterPointData] = await Promise.all([
              actions.loadChartData(
                siteId,
                duration,
                ['BESSkW', 'PVkW', 'WindkW'],
                'Site',
              ) as unknown as Promise<ChartDataPoint[]>,
              actions.loadChartData(
                useNewTopicStructure ? 'meter_1' : siteId,
                duration,
                chartMeterFields,
                'Meter',
              ) as unknown as Promise<ChartDataPoint[]>,
            ]);
            let newMeterPointData = meterPointData;

            if (meterPointDictionary) {
              newMeterPointData = meterPointData.map((el: ChartDataPoint) => {
                const value: ChartDataValue = {
                  Hz: el.value[chartMeterFields[0]],
                  kW: el.value[chartMeterFields[1]],
                  kVAR: el.value[chartMeterFields[2]],
                };
                return {
                  date: el.date,
                  value,
                };
              });
            }
            const compareFn = sitePointData.length > newMeterPointData.length;
            const baseArr = compareFn ? sitePointData : newMeterPointData;
            const joinArr = compareFn ? newMeterPointData : sitePointData;

            const res = baseArr.map((timeObj: ChartDataPoint) => ({
              ...timeObj,
              value: {
                ...timeObj.value,
                ...joinArr.find((o: ChartDataPoint) => o.date === timeObj.date)
                  ?.value,
              },
            }));
            return res;
          }}
          durationSwitches={['15M', '1H', '12H', '1D', '3D']}
        />
      </div>
    </div>
  );
}

export default storeConnector(MainChart, {
  config: ['siteMeta'],
});
